import { Badge, Box, Button, Menu, ScrollArea, Text, TextInput, Tooltip } from "@mantine/core";
import { IconCheck, IconCircleCheck, IconX } from "@tabler/icons-react";
import { useCallback, useMemo, useState } from "react";

// Simplified base props replacing IMcDropdownBase (we keep only needed ones)
interface IBaseProps {
  color?: string; // Mantine color name
  disabled?: boolean;
  withFilter?: boolean; // formerly filterActive
  tooltip?: string; // optional tooltip label
  menuWidth?: number;
  withinPortal?: boolean;
  variant?: string; // Mantine button variant, defaults to "secondary-filled"
  size?: "xs" | "sm" | "md" | "lg" | "xl";
  justify?: "flex-start" | "center" | "flex-end" | "space-between";
  multiSelect?: boolean;
  selectedValues?: any[];
  onMultiSelectChange?: (selected: any[]) => void;
  showSelectionBadge?: boolean;
  withSelectAll?: boolean;
  menuMaxHeight?: number;
  showSelectedSection?: boolean;
}

export type IMcDropdownGeneric<T extends Record<string, any>> = IBaseProps & {
  currentValue: T;
  onChange: (oldValue: T, newValue: T) => void;
  options: T[];
  displayKey: keyof T;
  selectedDisplayKey?: keyof T;
  type?: "text" | "colordot";
  defaultTitle?: string;
  formatter?: (option: T[keyof T]) => string;
};

export type IMcDropdownString = IBaseProps & {
  currentValue: string;
  onChange: (oldValue: string, newValue: string) => void;
  options: string[];
  type?: "text" | "colordot";
  defaultTitle?: string;
  formatter?: (option: string) => string;
};

export function McDropdownNew(props: IMcDropdownString): JSX.Element;
export function McDropdownNew<T extends Record<string, any>>(props: IMcDropdownGeneric<T>): JSX.Element;
export function McDropdownNew<T>(props: T extends string ? IMcDropdownString : IMcDropdownGeneric<T>): JSX.Element {
  const [filterValue, setFilterValue] = useState<string>("");
  const [opened, setOpened] = useState<boolean>(false);

  const {
    currentValue,
    onChange,
    options,
    displayKey,
    selectedDisplayKey,
    type = "text",
    defaultTitle,
    formatter,
    color = "",
    disabled = false,
    withFilter = false,
    tooltip,
    menuWidth = "auto",
    withinPortal = true,
    variant = "secondary-filled",
    size = "sm",
    justify = "center",
    multiSelect = false,
    selectedValues = [] as any[],
    onMultiSelectChange,
    showSelectionBadge,
    withSelectAll = true,
    menuMaxHeight = 220,
    showSelectedSection = false,
  } = props as IMcDropdownGeneric<T>;

  const identifierKey = (selectedDisplayKey ?? displayKey) as keyof T;

  const getIdentifier = useCallback(
    (option: T | string): string => {
      if (typeof option === "string") {
        return option;
      }
      if (option == null) {
        return "";
      }

      if (identifierKey && option[identifierKey] != null) {
        return String(option[identifierKey]);
      }

      if (displayKey && option[displayKey] != null) {
        return String(option[displayKey]);
      }

      return JSON.stringify(option);
    },
    [identifierKey, displayKey],
  );

  const optionsMap = useMemo(() => {
    const map = new Map<string, T>();
    (options || []).forEach((option) => {
      map.set(getIdentifier(option), option);
    });
    return map;
  }, [options, getIdentifier]);

  // Compute filtered options with useMemo instead of useEffect to avoid render loops
  const { filteredOptions, filteredLabels } = useMemo(() => {
    if (
      displayKey != null &&
      displayKey !== "" &&
      options != null &&
      options.length > 0 &&
      typeof options[0] === "object"
    ) {
      const opts = options.reduce(
        (acc, o) => {
          const value: string = formatter != null ? formatter(o[displayKey]) : String(o[displayKey]);
          if (
            filterValue === "" ||
            value.trim()?.toLocaleLowerCase().includes(filterValue.trim()?.toLocaleLowerCase())
          ) {
            acc[0].push(o);
            acc[1].push(value);
          }
          return acc;
        },
        [[], []] as [T[], string[]],
      );
      return { filteredOptions: opts[0], filteredLabels: opts[1] };
    } else if (options != null && options.length > 0 && typeof options[0] === "string") {
      const opts = (options as unknown as string[]).reduce(
        (acc, o) => {
          if (filterValue === "" || o.trim()?.toLocaleLowerCase().includes(filterValue.trim()?.toLocaleLowerCase())) {
            acc[0].push(o as unknown as T);
            acc[1].push(o);
          }
          return acc;
        },
        [[], []] as [T[], string[]],
      );
      return { filteredOptions: opts[0], filteredLabels: opts[1] };
    }
    return { filteredOptions: [], filteredLabels: [] };
  }, [options, filterValue, displayKey, formatter]);

  // Compute display value with useMemo instead of useEffect
  const displayCurrentValue = useMemo(() => {
    if (multiSelect) {
      return "";
    }

    try {
      if (
        selectedDisplayKey != null &&
        selectedDisplayKey !== "" &&
        currentValue != null &&
        typeof currentValue === "object"
      ) {
        return formatter == null
          ? String(currentValue[selectedDisplayKey]) || ""
          : formatter(currentValue[selectedDisplayKey]);
      } else if (displayKey != null && displayKey !== "" && currentValue != null && typeof currentValue === "object") {
        return formatter == null ? String(currentValue[displayKey]) || "" : formatter(currentValue[displayKey]);
      } else if (typeof currentValue === "string") {
        return formatter == null ? currentValue : formatter(currentValue[displayKey]);
      }
    } catch (error) {
      console.error(error);
    }
    return "";
  }, [currentValue, displayKey, selectedDisplayKey, formatter, multiSelect]);

  // Compute multi-selected IDs with useMemo instead of useEffect
  const multiSelectedIds = useMemo(() => {
    if (multiSelect && Array.isArray(selectedValues)) {
      return selectedValues.map((value) => getIdentifier(value as T));
    }
    return [];
  }, [multiSelect, selectedValues, getIdentifier]);

  const emitMultiSelectChange = useCallback(
    (ids: string[]) => {
      if (typeof onMultiSelectChange === "function") {
        const selectedOptions = ids.map((id) => optionsMap.get(id)).filter((option) => option != null) as T[];
        onMultiSelectChange(selectedOptions);
      }
    },
    [onMultiSelectChange, optionsMap],
  );

  const handleMultiToggle = useCallback(
    (option: T) => {
      const id = getIdentifier(option);
      const exists = multiSelectedIds.includes(id);
      const nextIds = exists ? multiSelectedIds.filter((value) => value !== id) : [...multiSelectedIds, id];
      emitMultiSelectChange(nextIds);
    },
    [getIdentifier, multiSelectedIds, emitMultiSelectChange],
  );

  const handleSelectAll = useCallback(() => {
    const allIds = (options || []).map((option) => getIdentifier(option));
    emitMultiSelectChange(allIds);
  }, [options, getIdentifier, emitMultiSelectChange]);

  const handleClearAll = useCallback(() => {
    emitMultiSelectChange([]);
  }, [emitMultiSelectChange]);

  const selectedCount = multiSelectedIds.length;
  const shouldShowBadge = showSelectionBadge ?? multiSelect;
  const shouldShowSelectedSection = multiSelect && showSelectedSection;

  const filteredEntries = useMemo(() => {
    return filteredOptions.map((option, index) => {
      const optionId = getIdentifier(option);
      return {
        option,
        optionLabel: filteredLabels[index],
        optionId,
        isSelected: multiSelect ? multiSelectedIds.includes(optionId) : filteredLabels[index] === displayCurrentValue,
      };
    });
  }, [filteredOptions, filteredLabels, getIdentifier, multiSelect, multiSelectedIds, displayCurrentValue]);

  const selectedEntries = useMemo(
    () =>
      shouldShowSelectedSection
        ? (options || []).reduce((acc, option) => {
            const optionId = getIdentifier(option);
            if (!multiSelectedIds.includes(optionId)) {
              return acc;
            }

            const optionLabel =
              typeof option === "string"
                ? formatter == null
                  ? option
                  : formatter(option as any)
                : formatter != null
                ? formatter(option[displayKey])
                : String(option[displayKey]);

            acc.push({ option, optionLabel, optionId, isSelected: true });
            return acc;
          }, [] as { option: T; optionLabel: string; optionId: string; isSelected: boolean }[])
        : [],
    [shouldShowSelectedSection, options, getIdentifier, multiSelectedIds, formatter, displayKey],
  );

  const unselectedEntries = useMemo(() => {
    if (!multiSelect) return filteredEntries;
    if (!showSelectedSection) return filteredEntries;
    return filteredEntries.filter((entry) => !entry.isSelected);
  }, [multiSelect, showSelectedSection, filteredEntries]);

  // Build trigger content
  const renderTriggerContent = () => {
    if (multiSelect) {
      return (
        <Box className="d-flex align-items-center" style={{ gap: 6 }}>
          <span className="truncateHeaderText" style={{ padding: "1px 0" }}>
            {defaultTitle ?? displayCurrentValue ?? "Velg"}
          </span>
          {shouldShowBadge && selectedCount > 0 && (
            <Badge size="xs" fz={"xs"} circle>
              {selectedCount}
            </Badge>
          )}
        </Box>
      );
    }

    if (type === "colordot") {
      const arr = options as unknown as (T & { color?: string })[];
      const colorValue = arr.find(
        (c) => String(c[selectedDisplayKey ?? displayKey]).toLowerCase() === displayCurrentValue?.toLowerCase(),
      )?.color;
      return (
        <Box className="d-flex align-items-center" style={{ gap: 6 }}>
          <Box className="statusdot" style={{ backgroundColor: colorValue || "transparent" }} />
          <span className="truncateHeaderText" style={{ padding: "1px 0" }}>
            {currentValue == null ? defaultTitle : displayCurrentValue}
          </span>
        </Box>
      );
    }
    return (
      <span className="truncateHeaderText" style={{ padding: "1px 0" }}>
        {currentValue == null ? defaultTitle : displayCurrentValue}
      </span>
    );
  };

  const handleSelect = useCallback(
    (index: number) => {
      onChange(currentValue as any, filteredOptions[index]);
      setOpened(false);
      setFilterValue("");
    },
    [onChange, currentValue, filteredOptions],
  );

  return (
    <Menu
      opened={opened}
      onChange={setOpened}
      styles={{ dropdown: { minWidth: 140 } }}
      width={menuWidth}
      shadow="md"
      withinPortal={withinPortal}
      position="bottom-start"
      closeOnItemClick={!multiSelect}
    >
      {tooltip ? (
        <Tooltip label={tooltip} withinPortal>
          <Menu.Target>
            <Button
              color={color}
              variant={variant}
              size={size}
              px={"sm"}
              disabled={disabled}
              onClick={() => setOpened((o) => !o)}
              rightSection={<i className={"fa fa-caret-down"} />}
              justify={justify}
            >
              {renderTriggerContent()}
            </Button>
          </Menu.Target>
        </Tooltip>
      ) : (
        <Menu.Target>
          <Button
            color={color}
            variant={variant}
            size={size}
            px={"sm"}
            disabled={disabled}
            onClick={() => setOpened((o) => !o)}
            rightSection={<i className={"fa fa-caret-down"} />}
            justify={justify}
          >
            {renderTriggerContent()}
          </Button>
        </Menu.Target>
      )}
      <Menu.Dropdown>
        {withFilter && (
          <TextInput
            autoFocus
            size="xs"
            placeholder="Søk..."
            value={filterValue}
            onChange={(e) => setFilterValue(e.currentTarget.value)}
            mb={4}
          />
        )}

        <ScrollArea style={{ maxHeight: menuMaxHeight, overflowY: "auto" }} type="auto">
          {shouldShowSelectedSection && selectedEntries.length > 0 && (
            <Text size="xs" c="dimmed" px="sm" pt="xs" pb={4}>
              Valgt ({selectedCount})
            </Text>
          )}

          {(shouldShowSelectedSection ? selectedEntries : unselectedEntries).map((entry, index) => {
            const { option, optionLabel, optionId, isSelected } = entry;

            const leftSection =
              type === "colordot" ? (
                <Box
                  className="statusdot"
                  style={{
                    backgroundColor: (option as any)?.color || "transparent",
                  }}
                />
              ) : undefined;

            const rightSection = multiSelect ? (
              isSelected && <IconCheck size={14} stroke={4} />
            ) : optionLabel === displayCurrentValue ? (
              // <i className="fa fa-check fa-fw" />
              <IconCheck size={14} stroke={4} />
            ) : null;

            return (
              <Menu.Item
                style={{ fontSize: "0.9rem" }}
                key={`${optionId}-${index}`}
                onClick={() => (multiSelect ? handleMultiToggle(option) : handleSelect(index))}
                leftSection={leftSection}
                rightSection={rightSection}
                color={isSelected ? "mc-primary" : undefined}
              >
                <span className="truncateHeaderText">{optionLabel}</span>
              </Menu.Item>
            );
          })}

          {shouldShowSelectedSection && selectedEntries.length > 0 && unselectedEntries.length > 0 && <Menu.Divider />}

          {shouldShowSelectedSection &&
            unselectedEntries.map((entry, index) => {
              const { option, optionLabel, optionId, isSelected } = entry;

              const leftSection =
                type === "colordot" ? (
                  <Box
                    className="statusdot"
                    style={{
                      backgroundColor: (option as any)?.color || "transparent",
                    }}
                  />
                ) : undefined;

              const rightSection = isSelected && <IconCheck size={14} stroke={4} />;

              return (
                <Menu.Item
                  style={{ fontSize: "0.9rem" }}
                  key={`${optionId}-unselected-${index}`}
                  onClick={() => handleMultiToggle(option)}
                  leftSection={leftSection}
                  rightSection={rightSection}
                  color={isSelected ? "mc-primary" : undefined}
                >
                  <span className="truncateHeaderText">{optionLabel}</span>
                </Menu.Item>
              );
            })}
        </ScrollArea>

        {multiSelect && withSelectAll && (
          <>
            <Menu.Divider />
            <Menu.Item style={{ fontSize: "0.9rem" }} onClick={handleClearAll} leftSection={<IconX size={14} />}>
              Avmarker alle
            </Menu.Item>
            <Menu.Item
              style={{ fontSize: "0.9rem" }}
              onClick={handleSelectAll}
              leftSection={<IconCircleCheck size={14} />}
            >
              Marker alle
            </Menu.Item>
          </>
        )}
      </Menu.Dropdown>
    </Menu>
  );
}

export default McDropdownNew;
